import React, { useEffect, useRef, useMemo, useState } from 'react';
import { SubmitError } from 'Components/Fields/SubmitError';
import { lifetimeYear, dayOfMonth, required, zip } from 'Utils/FormValidation';
import { SubmitButton } from 'Components/Fields/Buttons';
import { useForm } from 'Hooks/useForm';
import { FormField } from 'Components/HookFormFields/FormField';
import {
  ACTIVE_DUTY_STATUS,
  EMT_STATUS,
  FIREFIGHTER_STATUS,
  POLICE_STATUS,
  RESERVIST_STATUS,
  SHEER_FIRST_RESPONDER_TYPE,
  SHEER_MILITARY_TYPE,
  VETERAN_STATUS,
} from 'Utils/Constants';
import './SheerIdForm.css';
import { AsyncInputSearchField } from 'Components/HookFormFields/AsyncInputSearchField';

const STATUS_OPTIONS = {
  military: [
    {
      value: ACTIVE_DUTY_STATUS,
      label: 'Active Duty',
    },
    {
      value: VETERAN_STATUS,
      label: 'Military Veteran or Retiree',
    },
    {
      value: RESERVIST_STATUS,
      label: 'Reservist or National Guard',
    },
  ],
  firstResponder: [
    {
      value: POLICE_STATUS,
      label: 'Police ',
    },
    {
      value: EMT_STATUS,
      label: 'EMT',
    },
    {
      value: FIREFIGHTER_STATUS,
      label: 'Firefighter',
    },
  ],
};

const MONTH_OPTIONS = [
  {
    value: '01',
    label: 'January',
  },
  {
    value: '02',
    label: 'February',
  },
  {
    value: '03',
    label: 'March',
  },
  {
    value: '04',
    label: 'April',
  },
  {
    value: '05',
    label: 'May',
  },
  {
    value: '06',
    label: 'June',
  },
  {
    value: '07',
    label: 'July',
  },
  {
    value: '08',
    label: 'August',
  },
  {
    value: '09',
    label: 'September',
  },
  {
    value: '10',
    label: 'October',
  },
  {
    value: '11',
    label: 'November',
  },
  {
    value: '12',
    label: 'December',
  },
];

export const SheerIdForm = ({
  onFormSubmit,
  organizations = [],
  initialValues,
  remount,
  onSearchOrganization,
  currentOrgQuery = '',
  error = '',
}) => {
  const formName = 'sheerIdForm';
  const { formFieldProps, submitting, submitDisabled, formError, handleSubmit } = useForm({
    onFormSubmit,
    formName,
    initialValues,
  });
  const urlParams = new URLSearchParams(location.search);
  const sheerProgramType = urlParams.get('sheerVerification') || SHEER_MILITARY_TYPE;

  const prevShowDischargeDate = useRef();

  const { status, organization } = formFieldProps.fieldValues;
  const showDischargeDate = status === VETERAN_STATUS;

  const orgOptions = useMemo(
    () =>
      organizations
        .filter((org) => {
          let shouldShow = true;
          if (sheerProgramType === SHEER_MILITARY_TYPE) {
            const isReserveOrNGuard = org.name.includes('Reserve') || org.name.includes('National Guard');
            if (status === RESERVIST_STATUS) shouldShow = isReserveOrNGuard;
            if (status === ACTIVE_DUTY_STATUS) shouldShow = !isReserveOrNGuard;
          }
          return shouldShow;
        })
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((org) => {
          return { value: org.id.toString(), label: org.name };
        }),
    [organizations, status, sheerProgramType]
  );

  useEffect(() => {
    /* resetting form to clear any info in useForm relating to the discharge date fields or org field
    when they will no longer be valid in newest version of form */

    const clearDischargeDate = prevShowDischargeDate?.current && !showDischargeDate;
    const clearOrg = organization && !orgOptions.some((org) => org.value === organization);

    if (clearDischargeDate || clearOrg) {
      remount(formFieldProps.fieldValues, clearDischargeDate, clearOrg);
    } else {
      prevShowDischargeDate.current = showDischargeDate;
    }
  }, [showDischargeDate, organization, orgOptions, formFieldProps.fieldValues, remount]);

  return (
    <form onSubmit={(event) => handleSubmit(event)} className={formName}>
      <section className="formInput">
        <FormField
          name="status"
          label="Status"
          component="SelectField"
          validate={[required]}
          options={STATUS_OPTIONS[sheerProgramType]}
          {...formFieldProps}
        />
        {sheerProgramType === SHEER_FIRST_RESPONDER_TYPE ? (
          <AsyncInputSearchField
            name="organization"
            label="Type below to find your organization"
            validate={[required]}
            formFieldProps={formFieldProps}
            options={orgOptions}
            currentQuery={currentOrgQuery}
            onUpdateQuery={onSearchOrganization}
          />
        ) : (
          <FormField
            name="organization"
            label="Branch of Service"
            component="SelectField"
            validate={[required]}
            options={orgOptions}
            {...formFieldProps}
          />
        )}
        <div className="verificationFormRow">
          <FormField
            name="firstName"
            label="First Name"
            component="InputField"
            validate={[required]}
            autoComplete="given-name"
            {...formFieldProps}
          />
          <FormField
            name="lastName"
            label="Last Name"
            component="InputField"
            validate={[required]}
            autoComplete="family-name"
            {...formFieldProps}
          />
        </div>
        {showDischargeDate && (
          <>
            <div className="verificationRowTitle">Discharge Date</div>
            <div className="verificationFormRow">
              <FormField
                /* name may also be referenced in Page component and css */
                name="dischargeMonth"
                label="Month"
                component="SelectField"
                validate={[required]}
                options={MONTH_OPTIONS}
                {...formFieldProps}
              />
              <FormField
                /* name may also be referenced in Page component and css */
                name="dischargeDay"
                label="Day"
                component="InputField"
                validate={[required, dayOfMonth]}
                {...formFieldProps}
              />
              <FormField
                /* name may also be referenced in Page component and css */
                name="dischargeYear"
                label="Year"
                component="InputField"
                validate={[required, lifetimeYear]}
                {...formFieldProps}
              />
            </div>
          </>
        )}
        <div className="verificationRowTitle">Date of Birth</div>
        <div className="verificationFormRow">
          <FormField
            /* name may also be referenced in Page component and css */
            name="dobMonth"
            label="Month"
            component="SelectField"
            validate={[required]}
            options={MONTH_OPTIONS}
            {...formFieldProps}
          />
          <FormField
            /* name may also be referenced in Page component and css */
            name="dobDay"
            label="Day"
            component="InputField"
            validate={[required, dayOfMonth]}
            {...formFieldProps}
          />
          <FormField
            /* name may also be referenced in Page component and css */
            name="dobYear"
            label="Year"
            component="InputField"
            validate={[required, lifetimeYear]}
            {...formFieldProps}
          />
        </div>
      </section>
      <SubmitError error={error || formError} formName={formName} />
      <SubmitButton text="Continue for 1 Free Year" submitting={submitting} isDisabled={submitDisabled} modernLayout />
    </form>
  );
};
